// src/utils/estimateSave.js — doced-8f3a61c2 (Document editor, Stage 3: groundwork, no screen)
//
// THE save shape of an estimate, for the new full-page editor. Founder rule (2026-10-25): an estimate
// saved on the page must be EXACTLY what today's pop-up (src/components/crm/QuoteFormModal.js
// handleSave) would have written from the same editor state, so either screen can open and save the
// other's estimates and the switch can be turned back off with nothing lost.
//
// So this file reproduces the pop-up's save step by step, in the same order, including its quirks:
//   • every field of the loaded estimate rides through (the pop-up spreads it), minus the server-owned
//     ones it strips (deposit*, pipeline*, approval*, conversion*, `customer`) — even its own `id`;
//   • a "save to catalog" line that was saved keeps `addToCatalog: false`; one that failed keeps the tick;
//     every other line loses `addToCatalog`;
//   • the quote date is written from the date box (parseLocalDate), on every save;
//   • a blank expiry writes nothing — except a SEND, which re-dates a blank or past expiry;
//   • follow-up reminders start on Send or on moving into Sent; they stop on Approved / Declined;
//   • the deposit is written only while unlocked (no depositStatus yet), turned off = value 0; on with no
//     amount yet = value 0 + depositAmountNeeded: true (depdraft-3c8e5a14; the marker is always written).
// src/utils/estimateSave.test.js lifts the pop-up's own save code out of QuoteFormModal.js, runs it and
// this file on the same states, and fails if the two ever write anything different.
//
// Pure: Firestore's serverTimestamp, the date helpers and the line-ID helpers come in as deps.

// The fields the pop-up strips before writing (server-owned; the rules refuse a client write to them).
export const STRIPPED_FIELDS = Object.freeze([
    'deposit', 'depositStatus', 'depositRequired',
    'pipelineStageId', 'pipelineStageSetAt', 'outcomeType',
    'approvedAt', 'selectionsLocked', 'approvedItemIds',
    'declinedItemIds', 'approvedSubtotal', 'approvedTaxAmount',
    'approvedTotal', 'latestApprovalId',
    'convertedToJobId', 'convertedAt',
    'customer',
    'approvedTaxParts', 'approvedTotalsEngineVersion', // taxlines2-3e0b16a4 — the approval's tax parts: server-owned, never echoed
    'taxExemptionOff', 'taxExemptionHistory', // taxexempt3-8c4f2a1d — the per-document switch-off: server-owned, never echoed
]);

/** Which lines need a catalog item made first (the pop-up's "Save" tick on a line not from the catalog). */
export function linesNeedingCatalogItem(lines) {
    return (lines || []).map((item, i) => (item.addToCatalog && !item.serviceId && item.name ? i : -1)).filter((i) => i >= 0);
}

/** The catalog item the pop-up creates for such a line (addDoc to organizations/{org}/services). */
export function catalogItemForLine(item, serverTimestamp) {
    return {
        name: item.name,
        description: item.description || '',
        price: Number(item.price),
        type: item.type || 'Service',
        unit: 'Unit',
        taxCodeId: item.taxCodeId || '',
        createdAt: serverTimestamp(),
    };
}

/**
 * The lines as saved, after the catalog step. created = { [lineIndex]: newServiceId } for each catalog
 * item that WAS created; a line whose creation failed is simply absent (it keeps its tick, as the pop-up).
 */
export function linesAfterCatalogStep(lines, created = {}) {
    const need = new Set(linesNeedingCatalogItem(lines));
    return (lines || []).map((item, i) => {
        if (need.has(i)) {
            if (Object.prototype.hasOwnProperty.call(created, i) && created[i]) return { ...item, serviceId: created[i], addToCatalog: false };
            return item;
        }
        const { addToCatalog, ...cleanItem } = item; // eslint-disable-line no-unused-vars
        return cleanItem;
    });
}

/**
 * The document written to organizations/{org}/quotes/{id} (merge on edit; the create adds createFields).
 *
 * input = {
 *   quoteData,          the editor's state (same shape as the pop-up's quoteData: the loaded estimate
 *                       spread in, createdAt / expiresAt as 'YYYY-MM-DD' strings, lineItems)
 *   editingQuote,       the estimate as loaded (null / no id for a new one) — its status decides "moving into Sent"
 *   sendEmail,          true for Review & send
 *   totals,             { subtotal, taxAmount, total }
 *   processedLineItems, linesAfterCatalogStep(...)
 *   deposit,            { locked, type, value, enabled, required }  (value as typed, a string)
 *   today, defaultExpiry  'YYYY-MM-DD' (local day; default expiry = today + validity days)
 * }
 * deps = { serverTimestamp, parseLocalDate, formatLocalDate, ensureLineIds, newLineId }
 */
export function buildEstimateWrite(input, deps) {
    const { quoteData, editingQuote, sendEmail, totals, processedLineItems, deposit, today, defaultExpiry } = input;
    const { serverTimestamp, parseLocalDate, formatLocalDate, ensureLineIds, newLineId } = deps;

    const isBeingClosed = ['Approved', 'Declined'].includes(quoteData.status);
    const isTransitioningToSent = quoteData.status === 'Sent' && ((editingQuote && editingQuote.status) || '').toLowerCase() !== 'sent';

    const sendingNow = sendEmail || isTransitioningToSent;
    let expiresAtForSave = quoteData.expiresAt;
    if (sendingNow) {
        const existingDay = expiresAtForSave ? formatLocalDate(parseLocalDate(expiresAtForSave)) : null;
        if (!existingDay || existingDay < today) expiresAtForSave = defaultExpiry;
    }

    const quoteFields = {};
    for (const k of Object.keys(quoteData)) if (!STRIPPED_FIELDS.includes(k)) quoteFields[k] = quoteData[k];

    // depdraft-3c8e5a14 — ON with no amount (a Draft): value 0 + depositAmountNeeded: true; the marker is
    // written on every unlocked save (true or false), exactly as the pop-up.
    const depositAmountMissing = !(Number(deposit.value) > 0);
    const depositFields = deposit.locked ? {} : {
        deposit: {
            type: deposit.type,
            value: deposit.enabled && !depositAmountMissing ? Number(deposit.value) : 0,
        },
        depositRequired: deposit.enabled ? !!deposit.required : false,
        depositAmountNeeded: deposit.enabled && depositAmountMissing,
    };

    const dataToSave = {
        ...quoteFields,
        ...totals,
        lineItems: ensureLineIds(processedLineItems, newLineId),
        createdAt: parseLocalDate(quoteData.createdAt),
        expiresAt: parseLocalDate(expiresAtForSave),
        status: sendEmail ? 'Sent' : quoteData.status,
        updatedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        ...depositFields,
        ...((sendEmail || isTransitioningToSent) ? {
            sentAt: serverTimestamp(),
            followUpRemindersEnabled: true,
            followUpRemindersSent: [],
        } : {}),
        ...(isBeingClosed ? {
            followUpRemindersEnabled: false,
        } : {}),
    };
    if (!expiresAtForSave) delete dataToSave.expiresAt;
    return dataToSave;
}

/**
 * A NEW estimate's write: dataToSave plus what the pop-up stamps on create only. quoteNumber is NOT here:
 * the setQuoteNumberOnCreate trigger assigns it.
 * create = { docType (resolveDocTerminology(...).type), createdBy (the user's id), salesPersonId
 *            (resolveCreateSalesPersonId(customer, userId)), requestId (from a lead, else undefined) }
 */
export function buildEstimateCreate(dataToSave, create) {
    return {
        ...dataToSave,
        docType: create.docType,
        createdBy: create.createdBy,
        salesPersonId: create.salesPersonId,
        ...(create.requestId ? { requestId: create.requestId } : {}),
    };
}
